/**
 * 再生エンジン: 表示中のカードにだけ <video> を割り当て、
 * エピソード区間 [a,b] を録画レートそのままでループする。
 * 1カードに複数カメラがある場合は先頭カメラを基準に同期する。
 */
export class Engine {
  /** @param {number} cap 同時に生かすカード数（デコーダ保護） */
  constructor(cap = 12){ this.cap = cap; this.rate = 1; this.running = true; this.active = new Map(); }

  setRate(r){
    this.rate = r;
    for(const st of this.active.values()) st.vs.forEach(v => v.playbackRate = r);
  }
  setRunning(on){
    this.running = on;
    for(const st of this.active.values())
      st.vs.forEach(v => (on && !st.hold) ? v.play().catch(() => {}) : v.pause());
  }
  setCap(n){ this.cap = n; }

  /** @param {{stage:HTMLElement,url:string,a:number,b:number}[]} slots */
  attach(key, slots){
    if(this.active.has(key)) return;
    const st = { vs: [], slots, hold: false, poke: 0 };
    st.vs = slots.map(s => {
      const v = document.createElement('video');
      v.muted = true; v.playsInline = true; v.preload = 'auto'; v.disableRemotePlayback = true;
      v.addEventListener('loadedmetadata', () => { v.playbackRate = this.rate; v.currentTime = s.a; }, { once: true });
      // 区間の頭へ seek できてから見せる（共有ファイルの v3.0 で別エピソードの絵を出さない）。
      // 同じ位置への seek が seeked を出さない実装に備えて playing でも拾う。
      const show = () => v.classList.add('ready');
      v.addEventListener('seeked', show, { once: true });
      v.addEventListener('playing', show, { once: true });
      v.addEventListener('canplay', () => { if(this.running && !st.hold) v.play().catch(() => {}); });
      v.src = s.url;
      s.stage.appendChild(v);
      return v;
    });
    this.active.set(key, st);
  }

  detach(key){
    const st = this.active.get(key);
    if(!st) return;
    st.vs.forEach(v => { v.pause(); v.removeAttribute('src'); v.load(); v.remove(); });
    this.active.delete(key);
  }
  detachAll(){ for(const k of [...this.active.keys()]) this.detach(k); }

  hold(key, on){
    const st = this.active.get(key); if(!st || st.hold === on) return;
    st.hold = on;
    st.vs.forEach(v => on ? v.pause() : (this.running && v.play().catch(() => {})));
  }
  /** すべてのカードを区間の先頭（0:00:00）に戻す */
  restart(){
    for(const st of this.active.values()){
      st.hold = false;
      st.vs.forEach((v, k) => { v.currentTime = st.slots[k].a; });
    }
  }

  /**
   * 止まったままの <video> を拾い直す。毎フレーム呼ぶ。
   * play() は seek や src の差し替えで中断されると reject し、そのまま止まる。
   * 拒否は握りつぶしているので、ここで気付いて鳴らし直す。
   */
  poke(now){
    if(!this.running) return;
    for(const st of this.active.values()){
      if(st.hold || now < st.poke) continue;
      for(const v of st.vs){
        if(!v.paused || v.seeking || v.readyState < 2) continue;
        st.poke = now + 500;                      // 失敗しても毎フレーム叩かない
        v.play().catch(() => {});
      }
    }
  }

  scrub(key, f){
    const st = this.active.get(key); if(!st) return;
    st.vs.forEach((v, k) => { const s = st.slots[k]; v.currentTime = s.a + f * (s.b - s.a); });
  }

  /** 毎フレーム呼ぶ。区間ループとカメラ間のドリフト補正を行い、進捗 0..1 を返す */
  step(key){
    const st = this.active.get(key);
    if(!st) return null;
    const m = st.vs[0];
    if(m.readyState < 1 || m.seeking) return null;
    const s0 = st.slots[0];
    let f = (m.currentTime - s0.a) / (s0.b - s0.a || 1);
    if(st.hold) return clamp(f);
    if(f >= 1 || f < -0.05){
      st.vs.forEach((v, k) => v.currentTime = st.slots[k].a);
      return 0;
    }
    for(let k = 1; k < st.vs.length; k++){
      const v = st.vs[k];
      if(v.readyState < 1 || v.seeking) continue;
      const s = st.slots[k], tgt = s.a + f * (s.b - s.a);
      if(Math.abs(v.currentTime - tgt) > 0.15) v.currentTime = tgt;
    }
    return clamp(f);
  }
}
const clamp = f => Math.max(0, Math.min(1, f));
